// campaignContentApi — the in-app landing behind a campaign push.
//
//   GET /campaign-content/{accountId}/{cognitoId}?enrollment={id}&step={key}
//
// A member route on the client gateway, not an admin one: the path identifies
// the member reading their own campaign mail, and the backend scopes the
// enrollment lookup by that account.
//
// Every refusal is the same 404 with one message — an enrollment id is a small
// sequential integer, so distinguishing "wrong account" from "no such step"
// would let a caller map the campaign estate by counting. Treat any failure as
// "this message isn't available" and don't try to read more into it.

import { apiGet } from './apiClient';

export interface CampaignContentPush {
  title: string | null;
  body: string | null;
}

export interface CampaignContent {
  campaign: { key: string | null; name: string | null };
  step_key: string;
  /** Null on a push-only step. */
  subject: string | null;
  /** The merge-resolved email body, without the layout chrome, tracking pixel,
   *  click-redirects or unsubscribe footer. Null on a push-only step. */
  html: string | null;
  /** The notification copy as the member was sent it. Null when the step's copy
   *  was composed at send time and never persisted. */
  push: CampaignContentPush | null;
}

// The handler spreads the content over the envelope rather than nesting it
// under a payload key, so the fields sit at the top level next to `success`.
export type CampaignContentResponse = { success: boolean; message?: string } & Partial<CampaignContent>;

export async function fetchCampaignContent(
  accountId: string,
  cognitoId: string,
  opts: { enrollmentId: string | number; stepKey: string },
): Promise<CampaignContentResponse> {
  const qs = new URLSearchParams({
    enrollment: String(opts.enrollmentId),
    step: String(opts.stepKey),
  });
  return apiGet(`/campaign-content/${accountId}/${cognitoId}?${qs.toString()}`);
}
